import { canChangePrice, isParentScoped, isPendingChange, rulePrices, sellerGross, sellerGstOf, type PriceComponent, type PriceRule, type SellerGst } from "./priceList";
import { PROMOTION_REF } from "./promotion";
import type { RadGroup } from "./catalog";

/**
 * Plans › Plan details (owner 2026-10-15: "view/edit plan which is for php
 * portal, we want it in our ankhos react ... plan and components"). Pure: the
 * plan's price item, its price history and the add-ons it can be sold with.
 */
export interface PriceStep {
  from: string;
  to?: string;
  /** What the subscriber pays; undefined when the seller's GST is not known. */
  gross?: number;
  /** "festive" for a promotion, "price file" when compiled, else "typed by hand". */
  source: "festive" | "price file" | "typed by hand";
}

export interface PlanDetail {
  component?: PriceComponent;
  speedMbps?: string;
  termDays?: string;
  devices?: string;
  product?: string;
  history: PriceStep[];
  /** The rule Change price ends and replaces: the plan's own open fixed price, never a festive one. */
  changeable?: PriceRule;
  addons: PriceComponent[];
}

/** The plan's price item: the component compiled for its radcatalog group, by uid, else by name. */
export function planComponent(group: Pick<RadGroup, "groupuid" | "groupname">, components: PriceComponent[]): PriceComponent | undefined {
  return components.find(
    (x) => (group.groupuid && x.radcatalog_groupuid?.toLowerCase() === group.groupuid.toLowerCase()) || x.radcatalog_groupname === group.groupname,
  );
}

export type PlanPrice =
  | { gross: number; gst: number; ruleId: string; componentId: string }
  | { problem: "no price item" | "no price in force" | "several prices in force" | "GST not known" };

/**
 * The plan's price with GST at this instant (Reports › All plans, owner
 * 2026-10-29: the price left the plan description, so it is read from billing).
 * radbilling's own test, so the two cannot drift: active, effective_from <= now,
 * effective_to absent or > now -- the real instant, so a dated price turns at
 * 05:30 IST. Two rules in force for one plan are never resolved by guessing.
 * The GST is the seller of record's (`seller`, from billing's quote); a plan
 * whose rate billing did not state is "GST not known", never 18.
 */
export function priceInForce(
  group: Pick<RadGroup, "groupuid" | "groupname">,
  components: PriceComponent[],
  rules: PriceRule[],
  now: Date,
  seller: SellerGst,
): PlanPrice {
  const c = planComponent(group, components);
  if (!c) return { problem: "no price item" };
  const gst = sellerGstOf(c, seller);
  const t = now.getTime();
  const live = rules.filter(
    (r) => r.active && !isParentScoped(r) && rulePrices(r, c) &&
      Date.parse(r.effectivefrom) <= t && (!r.effectiveto || Date.parse(r.effectiveto) > t),
  );
  if (live.length > 1) return { problem: "several prices in force" };
  if (live.length === 0) return { problem: "no price in force" };
  if (gst === undefined) return { problem: "GST not known" };
  const r = live[0]!;
  const net = r.pricingmode === "ABSOLUTE" ? r.amount : c.price;
  return { gross: r.pricingmode === "FREE" ? 0 : sellerGross(net, gst), gst, ruleId: r.id, componentId: c.id };
}

export function planDetail(
  group: Pick<RadGroup, "groupuid" | "groupname">,
  components: PriceComponent[],
  rules: PriceRule[],
  now: Date = new Date(),
  /** The seller of record's GST for this plan (billing's quote); undefined = not known. */
  gst?: number,
): PlanDetail {
  const c = planComponent(group, components);
  const labels = c?.labels ?? {};
  const own = c
    ? rules
        .filter((r) => r.active && !isParentScoped(r) && rulePrices(r, c))
        .sort((a, b) => b.effectivefrom.localeCompare(a.effectivefrom))
    : [];
  // A change not started yet is moved or cancelled on the Price list, not changed again here.
  const changeable = own.find((r) => canChangePrice(r) && !r.catalogref?.startsWith(PROMOTION_REF) && !isPendingChange(r, now));
  const history: PriceStep[] = c
    ? own
        .map((r) => ({
          from: r.effectivefrom,
          ...(r.effectiveto ? { to: r.effectiveto } : {}),
          ...(gst === undefined ? {} : { gross: r.pricingmode === "FREE" ? 0 : sellerGross(r.pricingmode === "ABSOLUTE" ? r.amount : c.price, gst) }),
          source: r.catalogref?.startsWith(PROMOTION_REF) ? "festive" : r.catalogref ? "price file" : "typed by hand",
        }))
    : [];
  // Add-ons go with wired plans only (owner 2026-10-14, cart C3): a hotspot plan sells alone.
  const addons =
    c && labels.product !== "hotspot"
      ? components.filter((x) => x.active && x.labels?.kind === "addon").sort((a, b) => a.name.localeCompare(b.name))
      : [];
  return {
    ...(c ? { component: c } : {}),
    ...(labels.speed_mbps ? { speedMbps: labels.speed_mbps } : {}),
    ...(labels.term_days ? { termDays: labels.term_days } : {}),
    ...(labels.devices ? { devices: labels.devices } : {}),
    ...(labels.product ? { product: labels.product } : {}),
    history,
    ...(changeable ? { changeable } : {}),
    addons,
  };
}
